<script setup lang="ts">
import { HttpStatusCode, isAxiosError } from 'axios';
import { BAlert, BForm, BFormGroup, BFormInput, BModal } from 'bootstrap-vue-next';
import { ref } from 'vue';

import type { PostApiV1UsersByUsernameMentorsErrors, PostApiV1UsersByUsernameMentorsResponse } from '~/api/generated';
import { addMentor } from '~/api/users';
import LoadingButton from '~/common/components/LoadingButton.vue';

const blankForm = {
  mentor_username: '',
};
const form = ref({ ...blankForm });
const error = ref<string | null>(null);
const loading = ref(false);

const props = defineProps<{
  userHandle: string;
}>();

const model = defineModel<boolean>();
const emit = defineEmits<{ mentorshipAdded: [mentorship: PostApiV1UsersByUsernameMentorsResponse] }>();

const resetForm = () => {
  form.value = { ...blankForm };
  error.value = null;
};

const addMentorship = async () => {
  error.value = null;
  loading.value = true;

  try {
    const mentorship = await addMentor(props.userHandle, form.value.mentor_username);
    emit('mentorshipAdded', mentorship);
    model.value = false;
  } catch (err) {
    const response = isAxiosError<Partial<PostApiV1UsersByUsernameMentorsErrors[422]>>(err) ? err.response : undefined;

    if (response?.status === HttpStatusCode.NotFound) {
      error.value = 'User not found';
    } else if (response?.data.errors) {
      error.value = response.data.errors.join(', ');
    } else {
      error.value = 'Failed to add mentor';
    }
  } finally {
    loading.value = false;
  }
};
</script>

<template>
  <BModal
    v-model="model"
    title="Add mentorship"
    no-footer
    @hidden="resetForm"
  >
    <BAlert
      :model-value="!!error"
      variant="danger"
    >
      {{ error }}
    </BAlert>

    <BForm @submit.prevent="addMentorship">
      <BFormGroup
        label="Mentor username"
        class="mb-3"
      >
        <BFormInput
          v-model="form.mentor_username"
          required
        />
      </BFormGroup>

      <LoadingButton
        type="submit"
        class="float-end"
        variant="primary"
        :loading="loading"
      >
        Add mentor
      </LoadingButton>
    </BForm>
  </BModal>
</template>
